import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import AdminTable from 'common/AdminTable';
import TruncatedMessage from 'common/TruncatedMessage';
import { clearReelVideoStatus, getReelVideo, removeReelVideo, updateReelVideo } from 'slice/reelVideoSlice';
import {
  VIDEO_TYPE_OPTIONS,
  getAssetUrl,
  getInitialFormValues,
  getInitialVideoMode,
  isActiveValue,
  isManagedUploadPath,
  isVideoFileUrl,
} from './helpers';

const defaultFormValues = getInitialFormValues({});

const List = () => {
  const dispatch = useDispatch();
  const { data, error, lastAction, loading } = useSelector((state) => state.reelVideo);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [currentReel, setCurrentReel] = useState(null);
  const [videoMode, setVideoMode] = useState('url');
  const [formData, setFormData] = useState(defaultFormValues);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState('');
  const [thumbnailPreviewUrl, setThumbnailPreviewUrl] = useState('');

  useEffect(() => {
    dispatch(getReelVideo());
  }, [dispatch]);

  useEffect(() => {
    if (pendingDeleteId && lastAction === 'remove' && !loading && !error) {
      toast('Reel video deleted successfully!');
      dispatch(clearReelVideoStatus());
      setPendingDeleteId(null);
    }
  }, [dispatch, error, lastAction, loading, pendingDeleteId]);

  useEffect(() => {
    if (hasSubmitted && lastAction === 'update' && !loading && !error) {
      toast('Reel video updated successfully!');
      dispatch(clearReelVideoStatus());
      setHasSubmitted(false);
      setShowModal(false);
      setCurrentReel(null);
      setVideoMode('url');
      setFormData(getInitialFormValues({}));
    }
  }, [dispatch, error, hasSubmitted, lastAction, loading]);

  useEffect(() => {
    if (!formData.video) {
      setVideoPreviewUrl('');
      return undefined;
    }

    const objectUrl = URL.createObjectURL(formData.video);
    setVideoPreviewUrl(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [formData.video]);

  useEffect(() => {
    if (!formData.thumbnailFile) {
      setThumbnailPreviewUrl('');
      return undefined;
    }

    const objectUrl = URL.createObjectURL(formData.thumbnailFile);
    setThumbnailPreviewUrl(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [formData.thumbnailFile]);

  const handleEditClick = (reel) => {
    dispatch(clearReelVideoStatus());
    setCurrentReel(reel);
    setVideoMode(getInitialVideoMode(reel));
    setFormData(getInitialFormValues(reel));
    setHasSubmitted(false);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    dispatch(clearReelVideoStatus());
    setShowModal(false);
    setCurrentReel(null);
    setHasSubmitted(false);
    setVideoMode('url');
    setFormData(getInitialFormValues({}));
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: name === 'is_active' ? Number(value) : value,
    }));
  };

  const handleVideoModeChange = (event) => {
    const nextMode = event.target.value;
    setVideoMode(nextMode);
    setFormData((prevState) => ({
      ...prevState,
      video: null,
      video_url: nextMode === 'upload' ? '' : prevState.video_url,
      video_type: nextMode === 'upload' ? 'mp4' : prevState.video_type === 'mp4' ? 'youtube' : prevState.video_type,
    }));
  };

  const handleVideoFileChange = (event) => {
    const file = event.target.files?.[0] || null;

    setFormData((prevState) => ({
      ...prevState,
      video: file,
      video_type: 'mp4',
    }));
    setVideoMode('upload');
  };

  const handleThumbnailFileChange = (event) => {
    const file = event.target.files?.[0] || null;

    setFormData((prevState) => ({
      ...prevState,
      thumbnailFile: file,
    }));
  };

  const handleSave = () => {
    if (!currentReel) {
      return;
    }

    if (!formData.title.trim()) {
      toast('Title is required.');
      return;
    }

    if (videoMode === 'upload') {
      const canKeepExistingUpload = isManagedUploadPath(currentReel?.video_url);
      if (!formData.video && !canKeepExistingUpload) {
        toast('Please choose a video file.');
        return;
      }
    }

    if (videoMode === 'url' && !formData.video_url.trim()) {
      toast('Video URL is required.');
      return;
    }

    const payload = new FormData();
    payload.append('title', formData.title.trim());
    payload.append('description', formData.description.trim());
    payload.append('is_active', String(formData.is_active));

    if (videoMode === 'upload') {
      if (formData.video) {
        payload.append('video', formData.video);
      }
    } else {
      payload.append('video_url', formData.video_url.trim());
      payload.append('video_type', formData.video_type);
    }

    if (formData.thumbnailFile) {
      payload.append('thumbnail', formData.thumbnailFile);
    } else if (formData.thumbnail_url.trim()) {
      payload.append('thumbnail', formData.thumbnail_url.trim());
    } else if (currentReel?.thumbnail && !isManagedUploadPath(currentReel.thumbnail)) {
      payload.append('thumbnail', '');
    }

    setHasSubmitted(true);
    dispatch(updateReelVideo({ id: currentReel.id, data: payload }));
  };

  const renderVideoPreview = (reel) => {
    const videoUrl = getAssetUrl(reel?.video_url);

    if (!videoUrl) {
      return <span className="text-muted">No video</span>;
    }

    if (reel?.video_type === 'mp4' || isVideoFileUrl(videoUrl)) {
      return (
        <video controls width="210" height="120" className="rounded">
          <source src={videoUrl} />
          <track kind="captions" src="" label="Reel video preview captions" />
          Your browser does not support video playback.
        </video>
      );
    }

    return (
      <a href={videoUrl} target="_blank" rel="noreferrer" className="btn editbtn">
        Open Video
      </a>
    );
  };

  const currentVideoUrl =
    videoPreviewUrl ||
    (videoMode === 'upload'
      ? isManagedUploadPath(currentReel?.video_url)
        ? getAssetUrl(currentReel.video_url)
        : ''
      : formData.video_url.trim());

  const currentThumbnailUrl =
    thumbnailPreviewUrl ||
    (formData.thumbnail_url.trim()
      ? getAssetUrl(formData.thumbnail_url.trim())
      : isManagedUploadPath(currentReel?.thumbnail)
        ? getAssetUrl(currentReel.thumbnail)
        : '');

  const tableHeaders = [
    {
      Header: 'ID',
      accessor: 'id',
    },
    {
      Header: 'Title',
      accessor: 'title',
    },
    {
      Header: 'Description',
      accessor: 'description',
      Cell: ({ row }) => <TruncatedMessage text={row.original.description} maxLength={50} />,
    },
    {
      Header: 'Type',
      accessor: 'video_type',
    },
    {
      Header: 'Video',
      accessor: 'video_url',
      Cell: ({ row }) => renderVideoPreview(row.original),
    },
    {
      Header: 'Thumbnail',
      accessor: 'thumbnail',
      Cell: ({ row }) =>
        row.original.thumbnail ? (
          <img
            src={getAssetUrl(row.original.thumbnail)}
            alt={row.original.title || 'thumbnail'}
            width={90}
            className="img-thumbnail"
            style={{ objectFit: 'cover' }}
          />
        ) : (
          <span className="text-muted">No thumbnail</span>
        ),
    },
    {
      Header: 'Status',
      accessor: 'is_active',
      Cell: ({ row }) => (
        <span className={`badge ${isActiveValue(row.original.is_active) ? 'badge-success' : 'badge-secondary'}`}>
          {isActiveValue(row.original.is_active) ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      Cell: ({ row }) => (
        <>
          <button
            className="btn removebtn mx-1 mb-2"
            onClick={() => {
              setPendingDeleteId(row.original.id);
              dispatch(removeReelVideo(row.original.id));
            }}
          >
            Remove
          </button>
          <button className="btn editbtn mx-1 mb-2" onClick={() => handleEditClick(row.original)}>
            Edit
          </button>
        </>
      ),
    },
  ];

  return (
    <>
      <div className="container-fluid mt-5">
        <div className="mainheadig mx-4">
          <h4 className="text-white font-weight-bold">Reel Video List</h4>
        </div>

        {Array.isArray(data) && data.length > 0 ? (
          <AdminTable tableHeaders={tableHeaders} tableData={data} />
        ) : (
          <p className="mx-4 mt-4">{loading ? 'Loading reel videos...' : 'No reel videos available.'}</p>
        )}
      </div>

      {showModal && currentReel && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Reel Video</h5>
                <button type="button" className="close" onClick={handleCloseModal}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <form>
                  <div className="form-group">
                    <label htmlFor="editReelTitle">Title</label>
                    <input
                      id="editReelTitle"
                      name="title"
                      type="text"
                      className="form-control"
                      value={formData.title}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="editReelStatus">Status</label>
                    <select
                      id="editReelStatus"
                      name="is_active"
                      className="form-control"
                      value={formData.is_active}
                      onChange={handleChange}
                    >
                      <option value={1}>Active</option>
                      <option value={0}>Inactive</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="editReelDescription">Description</label>
                    <textarea
                      id="editReelDescription"
                      name="description"
                      rows={4}
                      className="form-control"
                      value={formData.description}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="editReelSource">Video Source</label>
                    <select
                      id="editReelSource"
                      className="form-control"
                      value={videoMode}
                      onChange={handleVideoModeChange}
                    >
                      <option value="upload">Upload File</option>
                      <option value="url">External URL</option>
                    </select>
                  </div>

                  {videoMode === 'url' && (
                    <div className="form-group">
                      <label htmlFor="editReelType">Video Type</label>
                      <select
                        id="editReelType"
                        name="video_type"
                        className="form-control"
                        value={formData.video_type}
                        onChange={handleChange}
                      >
                        {VIDEO_TYPE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {videoMode === 'upload' ? (
                    <div className="form-group">
                      <label htmlFor="editReelVideoFile">Video File</label>
                      <input
                        id="editReelVideoFile"
                        type="file"
                        name="video"
                        className="form-control btn-block filebg"
                        accept="video/mp4,video/webm,video/ogg"
                        onChange={handleVideoFileChange}
                      />
                    </div>
                  ) : (
                    <div className="form-group">
                      <label htmlFor="editReelVideoUrl">Video URL</label>
                      <input
                        id="editReelVideoUrl"
                        type="url"
                        name="video_url"
                        className="form-control"
                        value={formData.video_url}
                        onChange={handleChange}
                      />
                    </div>
                  )}

                  <div className="form-group">
                    <label htmlFor="editReelThumbnailFile">Thumbnail File</label>
                    <input
                      id="editReelThumbnailFile"
                      type="file"
                      name="thumbnailFile"
                      className="form-control btn-block filebg"
                      accept="image/*"
                      onChange={handleThumbnailFileChange}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="editReelThumbnailUrl">Thumbnail URL</label>
                    <input
                      id="editReelThumbnailUrl"
                      type="url"
                      name="thumbnail_url"
                      className="form-control"
                      value={formData.thumbnail_url}
                      onChange={handleChange}
                    />
                  </div>

                  {currentVideoUrl && (
                    <div className="form-group">
                      <div className="mb-2">Current Video</div>
                      <div>
                        {videoMode === 'upload' || isVideoFileUrl(currentVideoUrl) ? (
                          <video controls className="w-100 rounded" style={{ maxHeight: 240 }}>
                            <source src={currentVideoUrl} />
                            <track kind="captions" src="" label="Reel video preview captions" />
                            Your browser does not support video playback.
                          </video>
                        ) : (
                          <a href={currentVideoUrl} target="_blank" rel="noreferrer" className="btn editbtn">
                            Open Video
                          </a>
                        )}
                      </div>
                    </div>
                  )}

                  {currentThumbnailUrl && (
                    <div className="form-group">
                      <div className="mb-2">Current Thumbnail</div>
                      <div>
                        <img
                          src={currentThumbnailUrl}
                          alt={formData.title || 'thumbnail'}
                          className="img-thumbnail"
                          style={{ maxWidth: 220, width: '100%', objectFit: 'cover' }}
                        />
                      </div>
                    </div>
                  )}
                </form>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                  Close
                </button>
                <button type="button" className="btn btn-primary" onClick={handleSave} disabled={loading}>
                  Save changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default List;
